import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/observable/concat';
import 'rxjs/add/operator/takeUntil';
import {
  EmployeeStandardCellView,
  EmployeeStandardMatrixRequest,
  EmployeeStandardMatrixResponse,
  EmployeeStandardOverridesState,
  EmployeeStandardOverridesStatePatch,
  EmployeeStandardSelectableUser,
  EmployeeStandardRowView,
  StandardAssignmentCleanupState,
  StandardAssignmentCleanupView,
  EmployeeStandardUserResponse
} from '../../../../../../models/employee-standard-overrides.model';
import { EmployeeStandardOverridesService } from '../../../../../../services/employee-standard-overrides.service';
import { EmployeeStandardOverridesBulkRequestBuilder } from './employee-standard-overrides-bulk-request.builder';
import { EmployeeStandardOverridesMapper } from './employee-standard-overrides.mapper';
import { EmployeeStandardOverridesNotificationService } from './employee-standard-overrides-notification.service';
import {DepartmentReference} from 'app/models/group-context.model';
import {STANDARD_ASSIGNMENT_ERROR, STANDARD_ASSIGNMENT_ERROR_KEY, standardAssignmentErrorCode, standardAssignmentErrorKey} from 'app/services/standard-assignment-error';

const EMPLOYEE_STANDARD_STEP_SELECT = 'SELECT';
const EMPLOYEE_STANDARD_STEP_MATRIX = 'MATRIX';
const EMPTY_MATRIX_ERROR = '';
const CLEANUP_ACCESS_DENIED = 403;
const CLEANUP_BOOLEAN_TYPE = 'boolean';
const MIN_CLEANUP_VERSION = 0;
const MATRIX_REVIEW_CHANGES = 'STANDARD_ASSIGNMENT_REVIEW_CHANGES';

@Injectable()
export class EmployeeStandardOverridesFacade implements OnDestroy {
  public readonly state$: Observable<EmployeeStandardOverridesState>;
  private readonly stateSubject: BehaviorSubject<EmployeeStandardOverridesState>;
  private readonly destroy$ = new Subject<void>();
  private groupId = '';
  private matrixUserIds: string[] = [];
  private pendingIntent: EmployeeStandardCellView[] = [];
  private cleanupStandardIds: string[] = [];
  private destroyed = false;

  constructor(
    private readonly apiService: EmployeeStandardOverridesService,
    private readonly mapper: EmployeeStandardOverridesMapper,
    private readonly bulkRequestBuilder: EmployeeStandardOverridesBulkRequestBuilder,
    private readonly notificationService: EmployeeStandardOverridesNotificationService
  ) {
    this.stateSubject = new BehaviorSubject<EmployeeStandardOverridesState>(this.createInitialState());
    this.state$ = this.stateSubject.asObservable();
  }

  public initialize(groupId: string, departments?: DepartmentReference[]): void {
    if (this.destroyed) { return; }
    this.groupId = groupId;
    if (departments) { this.mapper.setDepartments(departments); }
    this.loadUsers();
  }

  public destroy(): void {
    if (this.destroyed) { return; }
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.stateSubject.complete();
  }

  public ngOnDestroy(): void {
    this.destroy();
  }

  public toggleUser(user: EmployeeStandardSelectableUser): void {
    if (this.destroyed || this.getState().isSaving || this.getState().isMatrixLoading) { return; }
    var users = this.mapper.toggleSelectableUsers(this.getState().users, user);
    this.patchState({
      users: users,
      selectedCount: this.mapper.countSelectedUsers(users)
    });
  }

  public openMatrix(): void {
    if (this.destroyed || this.getState().isSaving || this.getState().isMatrixLoading) { return; }
    this.matrixUserIds = this.createSelectedUserIds();
    if (!this.matrixUserIds.length) {
      this.notificationService.showNoSelection();
      return;
    }
    this.loadMatrix();
  }

  public openSelection(): void {
    if (this.destroyed || this.getState().isSaving || this.getState().isMatrixLoading) { return; }
    this.patchState({ step: EMPLOYEE_STANDARD_STEP_SELECT });
  }

  public toggleCell(cell: EmployeeStandardCellView): void {
    if (this.matrixBlocked()) { return; }
    var rows = this.mapper.toggleRows(this.getState().rows, cell);
    this.patchState({
      rows: rows,
      changedCount: this.mapper.countChangedCells(rows)
    });
  }

  public saveChanges(): void {
    if (this.matrixBlocked()) { return; }
    var changes = this.mapper.collectChangedCells(this.getState().rows);
    if (!changes.length) {
      return;
    }
    this.pendingIntent = changes.map(cell => Object.assign({}, cell));
    this.sendChanges(changes);
  }

  private matrixBlocked(): boolean {
    const state = this.getState();
    return this.destroyed || state.isSaving || state.isMatrixLoading || state.requiresReload || state.cleanupPending || state.cleanupLoadFailed;
  }

  public reloadMatrix(): void {
    if (this.destroyed || this.getState().isSaving || this.getState().isMatrixLoading) { return; }
    this.loadMatrix();
  }

  private loadUsers(): void {
    this.patchState({ isUsersLoading: true });
    this.apiService.fetchGroupUsers(this.groupId)
      .takeUntil(this.destroy$)
      .subscribe(
        (users) => this.handleUsersLoaded(users),
        (error) => this.handleLoadFailed(error)
      );
  }

  private handleUsersLoaded(users: EmployeeStandardUserResponse[]): void {
    var selectableUsers = this.mapper.mapSelectableUsers(users);
    this.patchState({
      users: selectableUsers,
      selectedCount: this.mapper.countSelectedUsers(selectableUsers),
      isUsersLoading: false
    });
  }

  private loadMatrix(): void {
    this.patchState(this.createMatrixLoadingPatch());
    this.apiService.fetchMatrix(this.groupId, this.createMatrixRequest())
      .takeUntil(this.destroy$)
      .subscribe(
        (matrix) => this.handleMatrixLoaded(matrix),
        (error) => this.handleLoadFailed(error)
      );
  }

  private handleMatrixLoaded(matrix: EmployeeStandardMatrixResponse): void {
    var users = this.resolveMatrixUsers(matrix);
    const rows = this.mapper.restoreChanges(this.mapper.createRows(matrix, users), this.pendingIntent);
    this.patchState({
      matrixUsers: this.mapper.mapMatrixUsers(users),
      rows,
      changedCount: this.mapper.countChangedCells(rows),
      requiresReload: false,
      errorKey: this.matrixReviewMessage(rows),
      isMatrixLoading: false,
      isSaving: false
    });
  }

  private sendChanges(changes: EmployeeStandardCellView[]): void {
    this.patchState({ isSaving: true });
    Observable.concat(...this.createUpdateRequests(changes))
      .takeUntil(this.destroy$)
      .subscribe(
        () => {},
        (error) => this.handleSaveFailed(error),
        () => this.handleSaveSucceeded()
      );
  }

  private handleSaveSucceeded(): void {
    if (this.destroyed) { return; }
    this.pendingIntent = [];
    this.patchState({ errorKey: EMPTY_MATRIX_ERROR, cleanupStates: [], cleanupPending: false });
    this.notificationService.showSaveSuccess();
    this.loadMatrix();
  }

  private handleSaveFailed(error: HttpErrorResponse): void {
    this.patchState({ isSaving: false, requiresReload: true, errorKey: standardAssignmentErrorKey(error) });
    this.notificationService.showSaveError(error);
    if (standardAssignmentErrorCode(error) === STANDARD_ASSIGNMENT_ERROR.cleanup) {
      this.cleanupStandardIds = Array.from(new Set(this.pendingIntent.map(cell => cell.standardId)));
      this.loadCleanupStatuses();
    }
  }

  private matrixReviewMessage(rows: EmployeeStandardRowView[]): string {
    if (this.getState().cleanupPending || this.getState().cleanupLoadFailed) { return this.getState().errorKey; }
    if (rows.some(row => row.cells.some(cell => cell.blockedChange))) { return STANDARD_ASSIGNMENT_ERROR_KEY.inherited; }
    return this.pendingIntent.length ? MATRIX_REVIEW_CHANGES : EMPTY_MATRIX_ERROR;
  }

  public loadCleanupStatuses(): void {
    if (this.destroyed || this.getState().isSaving || !this.cleanupStandardIds.length) { return; }
    this.patchState({ isSaving: true, cleanupLoadFailed: false });
    Observable.forkJoin(this.cleanupStandardIds.map(id => this.apiService.fetchCleanupStatus(id)))
      .takeUntil(this.destroy$).subscribe(responses => {
        this.acceptCleanupStatuses(responses.map(response => response.body));
      }, error => this.rejectCleanupStatus(error));
  }

  private acceptCleanupStatuses(states: StandardAssignmentCleanupState[]): void {
    if (!states.every(state => this.validCleanupStatus(state))) { this.rejectCleanupStatus(null); return; }
    const pending = states.some(state => state.assignmentCleanupPending);
    this.patchState({
      cleanupStates: states.map(state => this.cleanupView(state)),
      cleanupPending: pending,
      errorKey: pending ? STANDARD_ASSIGNMENT_ERROR_KEY.cleanup : EMPTY_MATRIX_ERROR,
      requiresReload: true,
      isSaving: false,
      cleanupLoadFailed: false
    });
  }

  private cleanupView(state: StandardAssignmentCleanupState): StandardAssignmentCleanupView {
    const row = this.getState().rows.find(item => item.id === state.standardId);
    return Object.assign({}, state, { standardLabel: row ? row.number || row.name : null });
  }

  private validCleanupStatus(state: StandardAssignmentCleanupState): boolean {
    return !!state && typeof state.assignmentCleanupPending === CLEANUP_BOOLEAN_TYPE
      && Number.isSafeInteger(state.version) && state.version >= MIN_CLEANUP_VERSION
      && this.cleanupStandardIds.some(id => id === state.standardId);
  }

  private rejectCleanupStatus(error: HttpErrorResponse): void {
    const errorKey = error && error.status === CLEANUP_ACCESS_DENIED
      ? STANDARD_ASSIGNMENT_ERROR_KEY.access : STANDARD_ASSIGNMENT_ERROR_KEY.cleanup;
    this.patchState({ isSaving: false, cleanupLoadFailed: true, errorKey });
  }

  public resumeCleanup(status: StandardAssignmentCleanupState): void {
    const states = this.getState().cleanupStates || [];
    if (this.destroyed || this.getState().isSaving || this.getState().cleanupLoadFailed || !states.some(current => current === status)) { return; }
    if (!status.assignmentCleanupPending) { return; }
    this.patchState({ isSaving: true });
    this.apiService.resumeCleanup(status).takeUntil(this.destroy$).subscribe(() => {
      this.patchState({ isSaving: false });
      this.loadCleanupStatuses();
    }, error => {
      this.patchState({ isSaving: false });
      this.notificationService.showSaveError(error);
      this.loadCleanupStatuses();
    });
  }

  private handleLoadFailed(error: HttpErrorResponse): void {
    this.patchState({
      requiresReload: this.getState().requiresReload || this.getState().step === EMPLOYEE_STANDARD_STEP_MATRIX,
      isUsersLoading: false,
      isMatrixLoading: false,
      isSaving: false
    });
    this.notificationService.showLoadError(error);
  }

  private createUpdateRequests(changes: EmployeeStandardCellView[]): Observable<string>[] {
    return this.bulkRequestBuilder.create(changes).map((request) => {
      return this.apiService.updateAssignments(this.groupId, request);
    });
  }

  private createSelectedUserIds(): string[] {
    return this.getState().users
      .filter((user) => user.isSelected)
      .map((user) => user.id);
  }

  private createMatrixRequest(): EmployeeStandardMatrixRequest {
    return {
      userIds: this.matrixUserIds.slice()
    };
  }

  private resolveMatrixUsers(matrix: EmployeeStandardMatrixResponse): EmployeeStandardUserResponse[] {
    if (!matrix || !matrix.users) {
      return [];
    }
    return matrix.users;
  }

  private createMatrixLoadingPatch(): EmployeeStandardOverridesStatePatch {
    return {
      step: EMPLOYEE_STANDARD_STEP_MATRIX,
      isMatrixLoading: true,
      changedCount: this.getState().changedCount
    };
  }

  private patchState(patch: EmployeeStandardOverridesStatePatch): void {
    this.stateSubject.next(Object.assign({}, this.getState(), patch));
  }

  private getState(): EmployeeStandardOverridesState {
    return this.stateSubject.getValue();
  }

  private createInitialState(): EmployeeStandardOverridesState {
    return {
      users: [],
      matrixUsers: [],
      rows: [],
      selectedCount: 0,
      changedCount: 0,
      isUsersLoading: false,
      isMatrixLoading: false,
      isSaving: false,
      step: EMPLOYEE_STANDARD_STEP_SELECT
    };
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-group/popups/employee-standard-overrides/services/employee-standard-overrides.facade.ts